import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Edit2, ArrowRight, Video, ExternalLink, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSeries() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSeries, setEditingSeries] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', published: true, image_url: '',
    video_title: '', video_goal: '', watch_url: '', embed_url: '', video_published: true,
    box_tool: '', box_toy: '', box_swag: '', box_description: ''
  });

  useEffect(() => { fetchSeries(); }, []);

  const fetchSeries = async () => {
    try {
      const response = await axios.get(`${API}/series`);
      const seriesData = response.data;
      // Fetch video for each series
      const enriched = await Promise.all(seriesData.map(async (s) => {
        const vidsRes = await axios.get(`${API}/videos/series/${s.id}`);
        return { ...s, video: vidsRes.data[0] || null };
      }));
      setSeries(enriched);
    } catch (error) {
      console.error('Error fetching series:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (s) => {
    setEditingSeries(s);
    setEditingVideo(s.video);
    setFormData({
      title: s.title,
      description: s.description,
      published: s.published,
      image_url: s.image_url || '',
      video_title: s.video?.title || '',
      video_goal: s.video?.goal_outcome || '',
      watch_url: s.video?.watch_url || '',
      embed_url: s.video?.embed_url || '',
      video_published: s.video?.published ?? true,
      box_tool: s.video?.box_tool || '',
      box_toy: s.video?.box_toy || '',
      box_swag: s.video?.box_swag || '',
      box_description: s.video?.box_description || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save series info
      await axios.put(`${API}/series/${editingSeries.id}`, {
        title: formData.title,
        description: formData.description,
        published: formData.published,
        image_url: formData.image_url || null,
        order_index: editingSeries.order_index
      });

      // Save video info
      if (editingVideo) {
        await axios.put(`${API}/videos/${editingVideo.id}`, {
          title: formData.video_title,
          goal_outcome: formData.video_goal,
          watch_url: formData.watch_url,
          embed_url: formData.embed_url,
          published: formData.video_published,
          order_index: editingVideo.order_index,
          box_tool: formData.box_tool,
          box_toy: formData.box_toy,
          box_swag: formData.box_swag,
          box_description: formData.box_description,
        });
      }

      toast.success('Content saved!');
      setEditingSeries(null);
      setEditingVideo(null);
      fetchSeries();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2">
          Content Library
        </h1>
        <p className="text-gray-500 font-body">
          Manage your training boxes, edit video links, descriptions, and box contents
        </p>
      </div>

      {/* Content Cards */}
      <div className="space-y-3">
        {series.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border-2 border-gray-100 p-5 hover:shadow-md transition-all" data-testid={`content-row-${s.order_index}`}>
            <div className="flex items-center gap-5">
              {/* Order */}
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center font-heading font-bold text-gray-600 flex-shrink-0">
                {s.order_index > 0 ? s.order_index : (s.phase === 'Featured' ? '★' : s.order_index)}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="font-heading font-bold text-gray-800 truncate">{s.title}</h3>
                  {s.phase && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-body flex-shrink-0">
                      {s.phase}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 font-body truncate">{s.description}</p>
              </div>

              {/* Vimeo Link */}
              <div className="hidden md:block flex-shrink-0 max-w-[250px]">
                {s.video?.watch_url ? (
                  <a href={s.video.watch_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-edupaws-sage hover:text-edupaws-navy font-body truncate">
                    <Video className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{s.video.watch_url}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                ) : (
                  <span className="text-xs text-gray-300 font-body">No video link</span>
                )}
              </div>

              {/* Status */}
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-body font-bold flex-shrink-0
                ${s.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {s.published ? 'Published' : 'Draft'}
              </span>

              {/* Edit Button */}
              <button
                onClick={() => openEditDialog(s)}
                data-testid={`edit-content-${s.order_index}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-edupaws-navy text-white rounded-xl font-body font-bold text-sm
                  hover:bg-edupaws-navy/90 transition-colors flex-shrink-0"
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog, Box + Video */}
      <Dialog open={!!editingSeries} onOpenChange={(open) => { if (!open) { setEditingSeries(null); setEditingVideo(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Edit: {editingSeries?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Box Info */}
            <div className="space-y-4">
              <h3 className="font-heading font-bold text-gray-700 text-sm uppercase tracking-wider">Box Info</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Box Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="edit-box-title" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Image URL</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..." data-testid="edit-box-image" className="h-11 rounded-xl" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="font-body font-bold text-sm">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="edit-box-description" className="rounded-xl resize-none" rows={2} />
              </div>
              <div className="flex items-center justify-between">
                <span className="font-body font-bold text-sm text-gray-700">Published</span>
                <Switch checked={formData.published} onCheckedChange={(c) => setFormData({ ...formData, published: c })}
                  data-testid="edit-box-published" />
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* Video Info */}
            {editingVideo && (
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-gray-700 text-sm uppercase tracking-wider">Video Content</h3>
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Video Title</Label>
                  <Input value={formData.video_title} onChange={(e) => setFormData({ ...formData, video_title: e.target.value })}
                    data-testid="edit-video-title" className="h-11 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Goal / Outcome</Label>
                  <Textarea value={formData.video_goal} onChange={(e) => setFormData({ ...formData, video_goal: e.target.value })}
                    data-testid="edit-video-goal" className="rounded-xl resize-none" rows={2} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Watch URL (Vimeo) *</Label>
                    <Input value={formData.watch_url} onChange={(e) => setFormData({ ...formData, watch_url: e.target.value })}
                      placeholder="https://vimeo.com/..." data-testid="edit-watch-url" className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Embed URL</Label>
                    <Input value={formData.embed_url} onChange={(e) => setFormData({ ...formData, embed_url: e.target.value })}
                      placeholder="https://player.vimeo.com/video/..." data-testid="edit-embed-url" className="h-11 rounded-xl" />
                  </div>
                </div>
              </div>
            )}

            <hr className="border-gray-100" />

            {/* Box Contents */}
            {editingVideo && (
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-gray-700 text-sm uppercase tracking-wider">What's in the Box</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Tool</Label>
                    <Input value={formData.box_tool} onChange={(e) => setFormData({ ...formData, box_tool: e.target.value })}
                      data-testid="edit-box-tool" className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Toy</Label>
                    <Input value={formData.box_toy} onChange={(e) => setFormData({ ...formData, box_toy: e.target.value })}
                      data-testid="edit-box-toy" className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-body font-bold text-sm">Swag</Label>
                    <Input value={formData.box_swag} onChange={(e) => setFormData({ ...formData, box_swag: e.target.value })}
                      data-testid="edit-box-swag" className="h-11 rounded-xl" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body font-bold text-sm">Box Description</Label>
                  <Textarea value={formData.box_description} onChange={(e) => setFormData({ ...formData, box_description: e.target.value })}
                    data-testid="edit-box-content-desc" className="rounded-xl resize-none" rows={2} />
                </div>
              </div>
            )}

            {/* Save */}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setEditingSeries(null); setEditingVideo(null); }}
                className="flex-1 font-body font-bold py-3 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="save-content-btn"
                className="flex-1 flex items-center justify-center gap-2 font-body font-bold py-3 rounded-xl bg-edupaws-navy text-white
                  hover:bg-edupaws-navy/90 transition-colors disabled:opacity-50">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
